import {KeyKind, TabMethod} from './share-widget-access.model';

/** A contact field a link carries as a URL parameter, as a tab's Fields table lists it. */
export interface AccessUrlField {
  key: string;
  label: string;
  /** What the parameter does, in the words of the tab listing it. */
  description: string;
  sample: string;
  /** Someone new needs it, so it starts ticked; it can be unticked for links to existing contacts. */
  neededForNew: boolean;
  /** Only someone new gets it; an existing contact keeps theirs. */
  newPeopleOnly: boolean;
  /** It only changes what the page shows and is never saved to the contact. */
  displayOnly: boolean;
  /** The key kinds it is listed for (Join/Update Via URL); listed for every key when unset. */
  keyKinds?: KeyKind[];
}

interface AccessUrlFieldDefinition {
  key: string;
  label: string;
  description: string;
  sample: string;
  /** The tabs that list it. */
  methods: TabMethod[];
  /** The tabs on which someone new needs it. */
  neededForNew?: TabMethod[];
  /** The tabs on which only someone new gets it. */
  newPeopleOnly?: TabMethod[];
  displayOnly?: boolean;
  keyKinds?: KeyKind[];
  /** Its description on a tab where the shared one does not fit. */
  descriptions?: Partial<Record<TabMethod, string>>;
}

const ALL_TABS: TabMethod[] = ['join', 'email', 'sso'];
// Without SSO, whose sign-in provider gives the name and the email.
const SIGNED_TABS: TabMethod[] = ['join', 'email'];
// Join/Update Via URL and SSO, which may add people. A PCI link (Access/Update via Email in URL)
// has the key in its path, and its saved fields never include the external ID or the referral.
const ADDING_TABS: TabMethod[] = ['join', 'sso'];
// SSO only ever gives a link's fields to someone new (the start link is unsigned).
const SSO_NEW_ONLY: TabMethod[] = ['sso'];

const SHARE_PAGE_LANGUAGE = 'Language the share page is shown in. It only changes the display and is not saved to the contact';

/** The Platform Unique ID field's sample, which a sample link with a Platform Unique ID key names. */
export const PLATFORM_ID_SAMPLE = 'ext-user-98765';

const CUSTOM_SAMPLES = [
  'User Message',
  'https://example.com/image.jpg',
  'Session: Keynote at 9 AM',
  'https://example.com/booth-logo.png',
  'https://example.com/promo-video.mp4',
  'Booth #214',
  'https://example.com/session-banner.jpg',
  'Track: Product Innovation',
  'https://example.com/company-logo.svg',
  'Join me at the networking lounge!'
];

// Every tab's contact fields, in table order. The campaign targeting rows (influencer type,
// segment code) are Join/Update's and SSO's own and close their tables; a PCI link never changes
// them. Descriptions and samples follow the widget instructions' attribute table.
// campaign_influencer_code is not offered.
const ACCESS_URL_FIELDS: AccessUrlFieldDefinition[] = [
  {
    key: 'first_name',
    label: 'First Name',
    description: 'Registrant first name',
    sample: 'John',
    neededForNew: ['join'],
    methods: SIGNED_TABS
  },
  {
    key: 'last_name',
    label: 'Last Name',
    description: 'Registrant last name',
    sample: 'Doe',
    neededForNew: ['join'],
    methods: SIGNED_TABS
  },
  {
    key: 'email',
    label: 'Email',
    description: 'Registrant email address',
    sample: 'john.doe@example.com',
    neededForNew: ['join'],
    // With an email key the email is the key itself.
    keyKinds: ['platformId'],
    methods: ['join']
  },
  {
    key: 'name',
    label: 'Full Name',
    description: 'Full name, for systems that do not keep first and last name separately. Someone new still needs first_name and last_name',
    descriptions: {email: 'Full name, for systems that do not keep first and last name separately'},
    sample: 'John Doe',
    methods: SIGNED_TABS
  },
  {
    key: 'headshot',
    label: 'Headshot',
    description: 'Profile photo URL (https; anything else is ignored)',
    descriptions: {sso: 'Profile photo URL (https; anything else is ignored). Used when the sign-in provider sends no picture'},
    sample: 'https://example.com/headshot.jpg',
    newPeopleOnly: SSO_NEW_ONLY,
    methods: ALL_TABS
  },
  {
    key: 'company',
    label: 'Company',
    description: 'Company or organization',
    sample: 'Acme Inc.',
    newPeopleOnly: SSO_NEW_ONLY,
    methods: ALL_TABS
  },
  {
    key: 'job_title',
    label: 'Job Title',
    description: 'Job title',
    sample: 'Marketing Director',
    newPeopleOnly: SSO_NEW_ONLY,
    methods: ALL_TABS
  },
  {
    key: 'booth_number',
    label: 'Booth Number',
    description: 'Booth or stand number assigned to the registrant',
    sample: 'A-12',
    newPeopleOnly: SSO_NEW_ONLY,
    methods: ALL_TABS
  },
  {
    key: 'promo_code',
    label: 'Promo Code',
    description: 'Promo or discount code assigned to the registrant',
    sample: 'SAVE20',
    newPeopleOnly: SSO_NEW_ONLY,
    methods: ALL_TABS
  },
  {
    key: 'badge_image_url',
    label: 'Badge Image URL',
    description: 'Badge image URL assigned to the registrant',
    sample: 'https://example.com/badge.png',
    newPeopleOnly: SSO_NEW_ONLY,
    methods: ALL_TABS
  },
  {
    key: 'platform_unique_id',
    label: 'Platform Unique ID',
    description: 'Your external system\'s unique ID for this registrant. Ignored when another contact already has this ID',
    sample: PLATFORM_ID_SAMPLE,
    newPeopleOnly: ['join'],
    // With a Platform Unique ID key the ID is the key itself.
    keyKinds: ['email'],
    methods: ['join']
  },
  ...CUSTOM_SAMPLES.map((sample, index): AccessUrlFieldDefinition => ({
    key: `custom${index + 1}`,
    label: `Custom ${index + 1}`,
    description: `Custom variable ${index + 1}: text, image URL, video URL, etc.`,
    sample,
    newPeopleOnly: SSO_NEW_ONLY,
    methods: ALL_TABS
  })),
  {
    key: 'referral',
    label: 'Referral',
    description: 'Referral code of the share link the registrant came through, so the referral is credited',
    sample: 'tr3Sdf',
    newPeopleOnly: ADDING_TABS,
    methods: ADDING_TABS
  },
  {
    key: 'referral_media',
    label: 'Referral Media',
    description: 'The channel that referral came through',
    sample: 'linkedin',
    newPeopleOnly: ADDING_TABS,
    methods: ADDING_TABS
  },
  {
    key: 'lang',
    label: 'Language',
    description: 'Language the widget renders in, overriding the one set in the widget settings. '
      + 'Useful on a localised version of your site',
    // SSO has no widget: the language is the share page's, as on Access/Update.
    descriptions: {email: SHARE_PAGE_LANGUAGE, sso: SHARE_PAGE_LANGUAGE},
    sample: 'fr',
    displayOnly: true,
    methods: ALL_TABS
  }
];

/** The fields a tab lists, in table order, each with that tab's description and flags. */
export function accessUrlFields(method: TabMethod): AccessUrlField[] {
  return ACCESS_URL_FIELDS
    .filter((field) => field.methods.includes(method))
    .map(({key, label, description, sample, descriptions, neededForNew, newPeopleOnly, displayOnly, keyKinds}) => ({
      key,
      label,
      description: descriptions?.[method] || description,
      sample,
      neededForNew: !!neededForNew?.includes(method),
      newPeopleOnly: !!newPeopleOnly?.includes(method),
      displayOnly: !!displayOnly,
      ...(keyKinds ? {keyKinds} : {})
    }));
}
